import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { useBusiness, useBusinessParameters } from '../hooks';
import { BusinessCooldownTimer, BusinessRequirements, BusinessRevenueInfo } from '../components';

export function BusinessDetailPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { business, loading, error } = useBusiness(businessId);
  const { parameters } = useBusinessParameters(business?.businessType);

  const currentParam = parameters.find((p) => p.level === business?.level);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-destructive">{error || 'Business not found'}</p>
        <Button variant="ghost" onClick={() => navigate('/a/arche_businesses')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/a/arche_businesses')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{business.name}</h1>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="capitalize">{business.businessType} Business</CardTitle>
            <BusinessCooldownTimer
              cooldownRemaining={business.cooldownRemaining}
              canCollect={business.canCollect}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Level</span>
              <p className="font-medium">{business.level}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status</span>
              <p className="font-medium capitalize">{business.status}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Collections Today</span>
              <p className="font-medium">{business.dailyCollectionsUsed} / 3</p>
            </div>
            <div>
              <span className="text-muted-foreground">Created</span>
              <p className="font-medium">{new Date(business.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {currentParam && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <BusinessRequirements parameter={currentParam} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <BusinessRevenueInfo parameter={currentParam} />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
